import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { useAuth } from '../../auth/auth-context';
import {
  geolocationPermission,
  isGeolocationSupported,
  readDevicePosition,
  readLocationPreference,
  writeLocationPreference,
  type GeolocationPermission,
  type LocationPreference,
} from '../../lib/device-location';
import { deviceLocationService } from '../../services/device-location.service';
import type { AccessLogLocation, DeviceLocationResponse } from '../../types';
import {
  DeviceLocationContext,
  type DeviceLocationStatus,
  type DeviceLocationValue,
} from './device-location-context';

/** A shared location is refreshed, on a project open, once it is this old. */
export const REFRESH_AFTER_MS = 30 * 60_000;
/** The longest a project open waits for a refresh before going ahead without it. */
export const OPEN_WAIT_MS = 2_500;

const STATUS_OF: Record<DeviceLocationResponse['status'], DeviceLocationStatus> = {
  RESOLVED: 'shared',
  IMPRECISE: 'imprecise',
  UNRESOLVED: 'unresolved',
};

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * Device location for the signed-in user (ADR-43). Mounted inside the app
 * shell, so only for authenticated screens. Nothing is read on page load: the
 * position is read when the user clicks "Share location", and refreshed only
 * when they open a project with sharing already on and allowed.
 */
export function DeviceLocationProvider({ children }: { readonly children: ReactNode }) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const supported = isGeolocationSupported();
  const [permission, setPermission] = useState<GeolocationPermission>('unknown');
  const [preference, setPreference] = useState<LocationPreference | null>(() =>
    userId === null ? null : readLocationPreference(userId),
  );
  const [status, setStatus] = useState<DeviceLocationStatus>('idle');
  const [place, setPlace] = useState<AccessLogLocation | null>(null);
  const sharedAt = useRef<number | null>(null);

  useEffect(() => {
    setPreference(userId === null ? null : readLocationPreference(userId));
    sharedAt.current = null;
    setPlace(null);
    setStatus('idle');
  }, [userId]);

  // Reading the permission state never prompts.
  useEffect(() => {
    if (!supported) {
      return;
    }
    let active = true;
    void geolocationPermission().then((state) => {
      if (active) setPermission(state);
    });
    return () => {
      active = false;
    };
  }, [supported]);

  const choose = useCallback(
    (next: LocationPreference): void => {
      setPreference(next);
      if (userId !== null) writeLocationPreference(userId, next);
    },
    [userId],
  );

  /** Reads the position and sends it. Returns where it ended; never throws. */
  const share = useCallback(async (): Promise<DeviceLocationStatus> => {
    const outcome = await readDevicePosition();
    if (outcome.kind !== 'position') {
      return outcome.kind;
    }
    try {
      const result = await deviceLocationService.share({
        latitude: outcome.latitude,
        longitude: outcome.longitude,
        accuracy: outcome.accuracy,
      });
      sharedAt.current = Date.now();
      setPlace(result.location);
      return STATUS_OF[result.status];
    } catch {
      return 'failed';
    }
  }, []);

  const enable = useCallback(async (): Promise<void> => {
    if (!supported) {
      setStatus('unsupported');
      return;
    }
    setStatus('requesting');
    const result = await share();
    setStatus(result);
    if (result === 'denied') {
      setPermission('denied');
      choose('denied');
    } else if (result !== 'unsupported') {
      choose('on');
      if (result === 'shared') setPermission('granted');
    }
  }, [choose, share, supported]);

  const disable = useCallback(async (): Promise<void> => {
    choose('off');
    sharedAt.current = null;
    setPlace(null);
    setStatus('idle');
    try {
      await deviceLocationService.clear();
    } catch {
      // The server forgets it anyway when it is two hours old or the session ends.
    }
  }, [choose]);

  const dismiss = useCallback((): void => {
    choose('dismissed');
  }, [choose]);

  const refreshBeforeOpen = useCallback(async (): Promise<void> => {
    if (preference !== 'on' || !supported) {
      return;
    }
    if (sharedAt.current !== null && Date.now() - sharedAt.current < REFRESH_AFTER_MS) {
      return;
    }
    // Only when the browser already allows it: opening a project never prompts.
    if ((await geolocationPermission()) !== 'granted') {
      return;
    }
    await Promise.race([
      share().then((result) => {
        setStatus(result);
      }),
      wait(OPEN_WAIT_MS),
    ]);
  }, [preference, share, supported]);

  const value = useMemo<DeviceLocationValue>(
    () => ({
      supported,
      permission,
      preference,
      status,
      place,
      enable,
      disable,
      dismiss,
      refreshBeforeOpen,
    }),
    [supported, permission, preference, status, place, enable, disable, dismiss, refreshBeforeOpen],
  );

  return <DeviceLocationContext value={value}>{children}</DeviceLocationContext>;
}
